'use client';

import Link from '@/components/ui/Link';
import { usePathname, useSearchParams } from '@/lib/router';
import { useState, type MouseEvent } from 'react';
import {
  ArrowsLeftRightIcon,
  CreditCardIcon,
  WalletIcon,
  TargetIcon,
  ChartLineIcon,
  LinkIcon,
  RepeatIcon,
  CaretDownIcon,
  HouseIcon,
  UserCircleIcon,
  SignOutIcon,
  UsersThreeIcon,
} from '@phosphor-icons/react';
import type { Icon as PhosphorIcon } from '@phosphor-icons/react';

import { cn } from '@/lib/utils';
import { track } from '@/lib/telemetry';
import { useIsMobile } from '@/hooks/use-mobile';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { ProblemReportDrawerEntry } from '@/components/problem-report/ProblemReportDrawerEntry';
import { useSpaceScope } from '@/contexts/SpaceScopeContext';
import { spaceNavItems, spacePageFromPath, spaceTelemetryTarget } from '@/lib/spaces/navigation';

type SubRoute = {
  href: string;
  label: string;
  icon: PhosphorIcon;
  match: (p: string) => boolean;
};

type Destination = {
  id: string;
  href: string;
  label: string;
  icon: PhosphorIcon;
  match: (p: string) => boolean;
  children?: SubRoute[];
};

const DESTINATIONS: Destination[] = [
  {
    id: 'home',
    href: '/',
    label: 'Início',
    icon: HouseIcon,
    match: (p) => p === '/' || p.startsWith('/connections'),
    children: [
      {
        href: '/',
        label: 'Início',
        icon: HouseIcon,
        match: (p) => p === '/',
      },
      {
        href: '/connections',
        label: 'Conexões',
        icon: LinkIcon,
        match: (p) => p.startsWith('/connections'),
      },
      {
        href: '/profile',
        label: 'Perfil',
        icon: UserCircleIcon,
        match: (p) => p.startsWith('/profile'),
      },
    ],
  },
  {
    id: 'transactions',
    href: '/transactions',
    label: 'Transações',
    icon: ArrowsLeftRightIcon,
    match: (p) => p.startsWith('/transactions'),
  },
  {
    id: 'budget',
    href: '/budget',
    label: 'Categorias',
    icon: WalletIcon,
    match: (p) => p.startsWith('/budget') || p.startsWith('/recurring-payments') || p.startsWith('/metas'),
    children: [
      {
        href: '/budget',
        label: 'Categorias',
        icon: WalletIcon,
        match: (p) => p.startsWith('/budget'),
      },
      {
        href: '/recurring-payments',
        label: 'Recorrentes',
        icon: RepeatIcon,
        match: (p) => p.startsWith('/recurring-payments'),
      },
      {
        href: '/metas',
        label: 'Metas',
        icon: TargetIcon,
        match: (p) => p.startsWith('/metas'),
      },
    ],
  },
  {
    id: 'faturas',
    href: '/invoices',
    label: 'Faturas',
    icon: CreditCardIcon,
    match: (p) => p.startsWith('/invoices') || p.startsWith('/faturas'),
  },
  {
    id: 'investments',
    href: '/investments',
    label: 'Investimentos',
    icon: ChartLineIcon,
    match: (p) => p.startsWith('/investments'),
  },
];

// A space has three destinations and the personal scope five; the bar spreads whichever it has across the width.
// Literal class names: Tailwind only generates the classes it finds written out.
const GRID_COLUMNS: Record<number, string> = { 3: 'grid-cols-3', 4: 'grid-cols-4', 5: 'grid-cols-5' };

export function BottomNavBar({ onLogout }: { onLogout: () => void }) {
  const { scope, spaceId, space } = useSpaceScope();
  const navigationKey = scope === 'space' ? `space:${spaceId}:${space?.model ?? 'loading'}` : 'personal';
  return <ScopedBottomNavBar key={navigationKey} onLogout={onLogout} />;
}

function ScopedBottomNavBar({ onLogout }: { onLogout: () => void }) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isMobile = useIsMobile();
  const [drawerFor, setDrawerFor] = useState<string | null>(null);
  const { scope, space, enabled } = useSpaceScope();
  const addRulesDestination = (destination: Destination): Destination => destination.id === 'home' && enabled
    ? { ...destination, children: [...destination.children ?? [], { href: '/spaces-rules', label: 'Espaços e regras', icon: UsersThreeIcon, match: (path) => path === '/spaces-rules' }] }
    : destination;
  const destinations: Destination[] = scope === 'space'
    ? space ? spaceNavItems(space, 'mobile', searchParams).map((item) => ({ ...item, match: (path) => spacePageFromPath(path) === item.id })) : []
    : DESTINATIONS.map(addRulesDestination);

  if (!isMobile) return null;

  const handleTabClick = (event: MouseEvent<HTMLAnchorElement>, dest: Destination) => {
    if (!dest.children?.length) {
      track('nav.clicked', { surface: 'bottom_nav', target: spaceTelemetryTarget(dest.href), scope });
      return;
    }
    event.preventDefault();
    setDrawerFor(dest.id);
  };

  const handleDrawerNavClick = (event: MouseEvent<HTMLAnchorElement>) => {
    const target = event.currentTarget.getAttribute('href');
    if (target) track('nav.clicked', { surface: 'bottom_nav_drawer', target: spaceTelemetryTarget(target), scope });
  };

  const handleDrawerOpenChange = (open: boolean) => {
    if (!open) setDrawerFor(null);
  };

  const drawerDest = destinations.find((d) => d.id === drawerFor && d.children) ?? null;

  return (
    <>
      <nav
        aria-label="Navegação principal"
        data-bottom-nav
        data-onboarding-mobile-nav
        className="fixed inset-x-0 bottom-0 z-40 border-t border-border-subtle bg-surface pb-[env(safe-area-inset-bottom)] md:hidden"
      >
        <ul role="list" className={cn('grid', GRID_COLUMNS[destinations.length] ?? 'grid-cols-5')}>
          {destinations.map((dest) => {
            const activeChild = dest.children?.find((child) => child.match(pathname));
            const active = Boolean(activeChild) || dest.match(pathname);
            const hasChildren = (dest.children?.length ?? 0) > 1;
            const isOpen = drawerFor === dest.id;
            const DisplayIcon = activeChild?.icon ?? dest.icon;
            const displayLabel = activeChild?.label ?? dest.label;

            return (
              <li key={dest.id} className="contents">
                <Link
                  href={dest.href}
                  onClick={(e) => handleTabClick(e, dest)}
                  data-bottom-nav-tab={dest.id}
                  data-bottom-nav-active-child={activeChild?.href}
                  data-onboarding-nav={dest.id}
                  role={hasChildren ? 'button' : undefined}
                  aria-current={active ? 'page' : undefined}
                  aria-haspopup={hasChildren ? 'menu' : undefined}
                  aria-expanded={hasChildren ? isOpen : undefined}
                  className="group relative flex h-14 touch-manipulation flex-col items-center justify-center gap-0.5 transition-transform active:scale-95"
                >
                  <span
                    className={cn(
                      'flex size-10 items-center justify-center rounded-full transition-colors',
                      active && 'bg-brand-50',
                    )}
                  >
                    <DisplayIcon
                      weight={active ? 'fill' : 'regular'}
                      className={cn(
                        'size-6 transition-colors',
                        active ? 'text-brand-700' : 'text-text-secondary group-hover:text-text-primary',
                      )}
                    />
                  </span>
                  <span
                    className={cn(
                      'flex max-w-full items-center gap-0.5 truncate px-1 text-2xs leading-none font-semibold transition-colors',
                      active ? 'text-brand-700' : 'text-text-secondary',
                    )}
                  >
                    <span className="truncate">{displayLabel}</span>
                    {hasChildren ? (
                      <CaretDownIcon
                        size={10}
                        weight="bold"
                        aria-hidden
                        className={cn(
                          'shrink-0 transition-transform',
                          isOpen && 'rotate-180',
                        )}
                      />
                    ) : null}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      <Drawer open={drawerDest !== null} onOpenChange={handleDrawerOpenChange}>
        <DrawerContent>
          <DrawerHeader className="text-left">
            <DrawerTitle>{drawerDest?.label ?? ''}</DrawerTitle>
            <DrawerDescription className="sr-only">
              Selecione uma sub-seção dentro de {drawerDest?.label ?? ''}.
            </DrawerDescription>
          </DrawerHeader>
          <div className="flex flex-col gap-1 px-2 pb-2">
            {drawerDest?.children?.map((child) => {
              const Icon = child.icon;
              const childActive = child.match(pathname);
              return (
                <DrawerClose asChild key={child.href}>
                  <Link
                    href={child.href}
                    onClick={handleDrawerNavClick}
                    data-onboarding-nav={child.href === '/connections' ? 'connections' : child.href.replace('/', '') || 'home'}
                    aria-current={childActive ? 'page' : undefined}
                    className={cn(
                      'flex w-full items-center gap-3 rounded-xl p-3 text-sm font-medium transition-colors active:bg-accent/80',
                      childActive
                        ? 'bg-brand-50 text-brand-700'
                        : 'text-text-primary hover:bg-accent',
                    )}
                  >
                    <Icon
                      size={20}
                      weight={childActive ? 'fill' : 'regular'}
                      className={childActive ? 'text-brand-700' : 'text-text-secondary'}
                    />
                    <span>{child.label}</span>
                  </Link>
                </DrawerClose>
              );
            })}
            {drawerDest?.id === 'home' ? <ProblemReportDrawerEntry /> : null}
            {drawerDest?.id === 'home' ? (
              <DrawerClose asChild>
                <button
                  type="button"
                  onClick={onLogout}
                  className="flex w-full cursor-pointer items-center gap-3 rounded-xl p-3 text-sm font-medium text-error-text transition-colors hover:bg-error-surface active:bg-error-surface"
                >
                  <SignOutIcon size={20} weight="regular" className="text-error-text" />
                  <span>Sair</span>
                </button>
              </DrawerClose>
            ) : null}
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
}
